import {
  incompleteClaimFields,
  withIncompleteClaimValidation,
} from './incompleteClaim';
import { z } from 'zod';
import { healthcardSchema } from './healthcard';
import { claimEditPatientSchema } from './claimEditPatient';
import { payorSchema } from './payor';
import { providerSchema } from './provider';
import { siteSchema } from './site';
import { submissionSchema } from './submission';
import {
  tariffSchemaWithOptionalICD9,
  tariffSchemaWithRequiredICD9,
} from './tariff';
import { debitSchema } from './debit';
import { baseClaimSchema } from './baseClaim';
import { remittanceSchema } from './remittance';
import { statementSchema } from './statement';
import { groupSchema } from './group';
import { validateClaimLocationOfService } from './claimLocationOfService';
import {
  requiresHospitalDetails,
  validateProvinceClaimFields,
} from './provinceClaimFields';

export const claimEditBaseSchema = baseClaimSchema.extend({
  internalComments: z.string().optional().nullable(),
  healthcard: z.lazy(() => healthcardSchema).nullable(),
  site: z.lazy(() => siteSchema),
  patient: z.lazy(() => claimEditPatientSchema).nullable(),
  provider: z.lazy(() => providerSchema),
  payor: z.lazy(() => payorSchema).nullable(),
  debits: z.lazy(() => debitSchema.array()).nullable(),
  claimHistories: z.array(z.unknown()),
  statements: z.lazy(() => statementSchema.array()),
  tariffs: z.lazy(() =>
    tariffSchemaWithOptionalICD9.array().min(1, 'Must have at least one tariff')
  ),
  submissions: z.lazy(() => submissionSchema.array()),
  remittances: z.lazy(() => remittanceSchema.array()).optional(),
  group: z
    .lazy(() => groupSchema)
    .optional()
    .nullable(),
});

type ClaimEditData = z.infer<typeof claimEditBaseSchema>;

export const validateClaimTariffIcd9 = (
  data: ClaimEditData,
  ctx: z.RefinementCtx
) => {
  const requiresTariffIcd9 = data.site?.requiresTariffIcd9 ?? true;
  if (!requiresTariffIcd9) {
    return;
  }

  data.tariffs.forEach((tariff, index) => {
    const parsedTariff = tariffSchemaWithRequiredICD9.safeParse(tariff);
    if (parsedTariff.success) {
      return;
    }

    parsedTariff.error.issues
      .filter((issue) => issue.path[0] === 'icd9')
      .forEach((issue) => {
        ctx.addIssue({
          code: 'custom',
          message: issue.message,
          path: ['tariffs', index, ...issue.path],
        });
      });
  });
};

export const validateClaimProviderGroup = (
  data: ClaimEditData,
  ctx: z.RefinementCtx
) => {
  // Group selection is site-specific. During form edits, `site` may reflect
  // the current selection before `siteID` has been synchronized, so prefer it.

  const hasProvider = !!data.provider && !!data.providerID;
  const providerGroups = data.provider?.groups ?? null;
  const effectiveSiteID = data.site?.id ?? data.siteID;
  const siteSpecificProviderGroups =
    hasProvider && Array.isArray(providerGroups)
      ? providerGroups.filter((group) => group.siteID === effectiveSiteID)
      : [];
  const hasGroups = siteSpecificProviderGroups.length > 0;

  // no provider -> groupID and group must be null
  if (!hasProvider) {
    if (data.groupID) {
      ctx.addIssue({
        code: 'custom',
        message: 'groupID must be null until a provider is selected',
        path: ['groupID'],
      });
    }
    if (data.group) {
      ctx.addIssue({
        code: 'custom',
        message: 'group must be null until a provider is selected',
        path: ['group'],
      });
    }
    return;
  }

  // provider has groups -> groupID required & must match one of them
  if (hasGroups) {
    if (!data.groupID) {
      ctx.addIssue({
        code: 'custom',
        message: 'Group is required for this provider',
        path: ['groupID'],
      });
    } else {
      const match = siteSpecificProviderGroups.some(
        (g) => g.id === data.groupID
      );
      if (!match) {
        ctx.addIssue({
          code: 'custom',
          message: 'groupID must match one of provider groups for this site',
          path: ['groupID'],
        });
      }
    }
  } else {
    // provider has no groups -> groupID must be null
    if (data.groupID) {
      ctx.addIssue({
        code: 'custom',
        message: 'groupID must be null because provider has no groups',
        path: ['groupID'],
      });
    }
    if (data.group) {
      ctx.addIssue({
        code: 'custom',
        message: 'group must be null because provider has no groups',
        path: ['group'],
      });
    }
  }
};

export const validateClaimHospitalFields = (
  data: ClaimEditData,
  ctx: z.RefinementCtx
) => {
  // If serviceLocationIndicator is set (not null/undefined and not blank '   ' or ' '),
  // then hospitalCode and hospitalName must both be populated (hospitalCode not 3 spaces)
  const hasServiceLocationIndicator = requiresHospitalDetails(data);

  if (hasServiceLocationIndicator) {
    const hospitalCode = data.hospitalCode;
    const hospitalName = data.hospitalName;
    const hospitalCodeIsBlank = !hospitalCode || /^\s{3}$/.test(hospitalCode);
    const hospitalNameIsBlank = !hospitalName || /^\s{3}$/.test(hospitalName);

    // If only one of hospitalCode or hospitalName is blank and not the other,
    // add a distinct issue for the blank one
    if (hospitalCodeIsBlank && !hospitalNameIsBlank) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Hospital Code is required when Service Location Indicator is set',
        path: ['hospitalCode'],
      });
    }
    if (hospitalNameIsBlank && !hospitalCodeIsBlank) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Hospital Name is required when Service Location Indicator is set',
        path: ['hospitalName'],
      });
    }

    // If either hospitalCode or hospitalName is blank, add a general issue for both
    if (hospitalCodeIsBlank || hospitalNameIsBlank) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Hospital Name is required when Service Location Indicator is set',
        path: ['hospitalName'],
      });
    }
  }
};

export const claimEditSchema = claimEditBaseSchema
  .superRefine(validateClaimTariffIcd9)
  .superRefine(validateClaimProviderGroup)
  .superRefine(validateClaimLocationOfService)
  .superRefine(validateClaimHospitalFields)
  .superRefine(validateProvinceClaimFields);

export const claimEditSaveSchema = withIncompleteClaimValidation(
  claimEditSchema,
  claimEditBaseSchema.extend(incompleteClaimFields)
);
